import {
  Children,
  forwardRef,
  useId,
  useMemo,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import { isElement } from 'react-is';

import type {
  AriaDisabledProps,
  AriaLabelingProps,
  DataTestId,
  DOMProps,
  MaskingProps,
  StylingProps,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';

import { Content } from './components/Content.js';
import { NotifyButtonTrigger } from './components/Trigger.js';
import { NotifyButtonContext } from './contexts/NotifyButtonContext.js';
import * as styles from './NotifyButton.sty.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { Content as NotificationSettingsContent } from '../../notifications/notification-settings/components/Content.js';
import { NotificationPermissionInfo } from '../../notifications/notification-settings/components/PermissionInfo.js';
import { NotificationToggleButton } from '../../notifications/notification-settings/components/ToggleButton.js';
import {
  NotificationProvider,
  type NotificationSettingsConfig,
} from '../../notifications/notification-settings/NotificationSettings.js';
import { useOverlayWithTrigger } from '../../overlays/hooks/useOverlayWithTrigger.js';
import { Overlay } from '../../overlays/overlay/Overlay.js';

//#region Typings

/**
 * Accepted properties for the NotifyButton
 * @public
 */
export type NotifyButtonProps = DOMProps &
  StylingProps &
  DataTestId &
  MaskingProps &
  PropsWithChildren &
  AriaLabelingProps &
  AriaDisabledProps &
  NotificationSettingsConfig & {
    /**
     * Configures the size of the trigger.
     * @defaultValue 'default
     */
    size?: 'default' | 'condensed';
    /**
     * Configures the variant of the trigger.
     * @defaultValue 'default
     */
    variant?: 'default' | 'emphasized';
    /**
     * Whether the NotifyButton is read-only.
     * @defaultValue false
     */
    readOnly?: boolean;
    /**
     * Whether the NotifyButton is disabled.
     * @defaultValue false
     */
    disabled?: boolean;
    /**
     * Whether the label text is shown on the trigger.
     * @defaultValue false
     */
    showLabel?: boolean;
  };

//#endregion

/** Iterates over child nodes to extract the content component slot. */
function getContentSlot(children: ReactNode) {
  let content: ReactNode;

  Children.forEach(children, (child) => {
    if (isElement(child)) {
      switch (child.type) {
        case Content:
          content = child;
          break;
        default: {
          if (isDev()) {
            // eslint-disable-next-line no-console
            console.warn('Only Content is a valid child of the NotifyButton.');
          }
          break;
        }
      }
    } else if (child) {
      if (isDev()) {
        // eslint-disable-next-line no-console
        console.warn('The used child is not supported by the NotifyButton.');
      }
    }
  });

  return content;
}

/**
 * NotifyButton component
 */
const RootNotifyButton = forwardRef<HTMLButtonElement, NotifyButtonProps>(
  (props, ref) => {
    const {
      children,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      className: consumerClassName,
      style: consumerStyle,
      disabled,
      readOnly,
      size = 'default',
      variant = 'default',
      type,
      config,
      showLabel = false,
      ...remainingProps
    }: NotifyButtonProps = props;

    const overlayAriaLabelledBy = useId();

    const {
      targetRef,
      triggerRef,
      overlayRef,
      overlayProps,
      overlayState,
      overlayTriggerProps,
    } = useOverlayWithTrigger({
      placement: 'bottom-start',
      placementFallbacks: [
        'bottom-start',
        'bottom-end',
        'top-start',
        'top-end',
      ],
      offset: 4,
      disabled,
      'aria-labelledby': overlayAriaLabelledBy,
    });
    const rootRef = useMergeRefs([ref, triggerRef, targetRef]);
    const contentSlot = useMemo(() => getContentSlot(children), [children]);

    const notifyButtonContextValue = useMemo(
      () => ({
        closeOverlay: overlayState.close,
        overlayAriaLabelledBy,
      }),
      [overlayAriaLabelledBy, overlayState.close],
    );

    return (
      <NotifyButtonContext.Provider value={notifyButtonContextValue}>
        <NotificationProvider type={type} config={config}>
          <NotifyButtonTrigger
            ref={rootRef}
            data-testid={dataTestId}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
            className={consumerClassName}
            style={consumerStyle}
            disabled={disabled}
            size={size}
            variant={variant}
            showLabel={showLabel}
            triggerProps={overlayTriggerProps}
            {...remainingProps}
          />
          <Overlay
            ref={overlayRef}
            isOpen={overlayState.isOpen}
            className={styles.overlay}
            overlayProps={overlayProps}
          >
            <Flex flexDirection="column" gap={12}>
              {contentSlot ?? <NotificationSettingsContent />}
              <Flex alignItems="center">
                <NotificationToggleButton />
                <NotificationPermissionInfo />
              </Flex>
            </Flex>
          </Overlay>
        </NotificationProvider>
      </NotifyButtonContext.Provider>
    );
  },
);

(
  RootNotifyButton as typeof RootNotifyButton & { displayName: string }
).displayName = 'NotifyButton';

/**
 * The `NotifyButton` allows users to turn notifications about updates of resources on or off. Common use cases include notifications about updates of saved filters, workflows, or entities. Users will be notified via e-mail.
 * @public
 */
export const NotifyButton = Object.assign(RootNotifyButton, {
  Content,
});
